import React from 'react';
import { PiArrowLeft } from 'react-icons/pi';
import Button from '../../atoms/buttons/button';
import { Text } from '../../atoms/text';
import { PanelHeader } from '../panel/panel-header';

interface PageHeaderProps {
  /** Page title. */
  title: string;
  /** Optional supporting content shown below the title. */
  subtitle?: React.ReactNode;
  /** Optional visual shown beside the title. */
  icon?: React.ReactNode;
  /** Optional back-navigation callback. */
  onBack?: () => void;
  /** Accessible label for the back action. */
  backLabel?: string;
  /** Optional right-aligned page actions. */
  actions?: React.ReactNode;
}

/**
 * Shared page-level header for route screens that need a back action, title,
 * subtitle, and right-aligned actions.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  onBack,
  backLabel,
  actions,
}) => {
  return (
    <PanelHeader
      className="border-b px-4 py-3"
      containerClassName="gap-3 px-0 py-0"
      childrenClassName="min-w-0"
      actionsClassName="ml-auto w-full justify-end sm:w-auto"
      actions={actions}
    >
      <div className="flex min-w-0 items-start gap-3 sm:items-center">
        {onBack ? (
          <Button
            variant="neutral"
            appearance="ghost"
            size="sm"
            isIconOnly
            aria-label={backLabel}
            onClick={onBack}
            className="mt-0.5 sm:mt-0"
          >
            <PiArrowLeft className="h-5 w-5" />
          </Button>
        ) : null}

        {icon ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-900">
            {icon}
          </span>
        ) : null}

        <div className="min-w-0 flex-1">
          <Text
            as="h2"
            size="h3"
            weight="medium"
            className="break-words text-slate-900"
          >
            {title}
          </Text>
          {subtitle ? (
            <Text size="small" className="mt-1 break-words text-slate-500">
              {subtitle}
            </Text>
          ) : null}
        </div>
      </div>
    </PanelHeader>
  );
};
